import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import {
  OKROwnerAndCollaboratorComponent,
  OWNER_TYPES,
} from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/OKROwnerAndCollaborator';
import { OKRTags } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/OKRTags';
import { isDependentType } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/OKRTrackingTypeSection';
import useOKRConfig from 'components/hooks/useOKRConfig';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';

import {
  CalendarThin,
  Collaborators,
  DepartmentThin,
  Description,
  PrivateObjective,
  Tags,
  WeightageTracking,
} from 'assets/assetsV1/iconComponents';
import { DepartmentName } from 'assets/assetsV1/iconComponents/DepartmentName';

import {
  GOAL_VISIBILITY_OPTIONS,
  OBJECTIVE_LEVEL,
  OBJECTIVE_TYPES_MAP,
  OKR_PROGRESS_TYPES,
  OKR_TYPES,
} from 'constants/ProjectConstants';

import { primaryDrawerWidth } from 'uiToolkitV1/BottomDrawer';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextLabel } from 'uiToolkitV1/Typography';

export const allLevels = [
  {
    value: 'COMPANY',
    label: 'Company',
  },
  {
    value: 'DEPARTMENT',
    label: 'Department',
  },
  {
    value: 'TEAM',
    label: 'Team',
  },
  {
    value: 'INDIVIDUAL',
    label: 'Individual',
  },
];

const OtherDetails = () => {
  const okrDetails = useSelector(okrCreationPanelStateSelector);
  const editOKRSavedDetails = useSelector(kRDrawerSelector);

  const {
    description,
    visibilityType,
    toIds,
    startTS,
    dueTS,
    objectiveLevel,
    progressType,
    tagKeyList: customTags,
    departmentName,
  } = okrDetails;

  const formattedStartDate = startTS ? format(new Date(startTS), 'MMM dd, yyyy') : null;

  const formattedDueDate = dueTS ? format(new Date(dueTS), 'MMM dd, yyyy') : null;

  const visibilityLabel = GOAL_VISIBILITY_OPTIONS.find((option) => option.value === visibilityType)?.label;

  const collaborators = toIds
    .filter(({ ownerType }) => ownerType === OWNER_TYPES.COLLABORATOR)
    .map((emp) => ({ ...emp, isSelected: true }));

  const collaboratorsExist = collaborators.length > 0;

  const levelLabel = allLevels?.find((level) => level?.value === objectiveLevel);

  const showTags = customTags?.length > 0;

  const showDescription = description?.length > 0;

  const isObjective = okrDetails?.type === OKR_TYPES.OBJECTIVE;

  const { OBJECTIVE_TYPE } = useOKRConfig();

  const isDetailedObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DETAILED.value;

  const isCompactObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.COMPACT.value;

  const showOwnersAndCollaborators = !isObjective || isCompactObjectiveType || isDetailedObjectiveType;

  const showTrackingSection = !isObjective || isDetailedObjectiveType;

  return (
    <Box>
      <Flex padding="8px 12px" alignItems="center">
        <Tooltip label="Time Period" shouldWrapChildren>
          <CalendarThin boxSize="24px" color="text.g2" fill="none" />
        </Tooltip>
        <TextLabel ml="8px" color="text.g2">
          {formattedStartDate} to {formattedDueDate}
        </TextLabel>
      </Flex>
      <Flex padding="8px 12px" alignItems="center">
        <Tooltip label="Visibility" shouldWrapChildren>
          <PrivateObjective boxSize="24px" color="text.g2" />
        </Tooltip>
        <TextLabel ml="8px" color="text.g2">
          {visibilityLabel}
        </TextLabel>
      </Flex>
      {showOwnersAndCollaborators && (
        <Flex padding="8px 12px" alignItems="center">
          <Tooltip label="Collaborators" shouldWrapChildren>
            <Collaborators fill="transparent" boxSize="24px" color="text.g2" />
          </Tooltip>
          {collaboratorsExist ? (
            <Box ml="8px">
              <OKROwnerAndCollaboratorComponent
                isOwner={false}
                ownersAndCollaborators={toIds}
                isReadOnly
                showName={false}
                showSelectEmptyMessage={false}
              />
            </Box>
          ) : (
            <TextLabel ml="8px" color="text.g2">
              No Collaborators{' '}
            </TextLabel>
          )}
        </Flex>
      )}
      {isObjective === true && (
        <Flex padding="8px 12px" alignItems="center">
          <Tooltip label="Level" shouldWrapChildren>
            <DepartmentThin boxSize="24px" color="text.g2" />
          </Tooltip>
          <TextLabel ml="8px" color="text.g2">
            {levelLabel?.label}
          </TextLabel>
        </Flex>
      )}
      {isObjective === true && objectiveLevel === OBJECTIVE_LEVEL.DEPARTMENT && departmentName ? (
        <Flex padding="8px 12px" alignItems="center">
          <Tooltip label="Department" shouldWrapChildren>
            <DepartmentName boxSize="24px" color="text.g2" fill="none" />
          </Tooltip>
          <TextLabel ml="8px" color="text.g2">
            {departmentName}
          </TextLabel>
        </Flex>
      ) : null}
      {showTrackingSection && (
        <Flex padding="8px 12px" alignItems="center">
          <Tooltip label="Tracking Type" shouldWrapChildren>
            <WeightageTracking boxSize="24px" color="text.g2" fill="none" />
          </Tooltip>

          <TextLabel ml="8px" color="text.g2">
            {`${
              isDependentType(progressType)
                ? `Dependent tracking computed as  ${
                    progressType === OKR_PROGRESS_TYPES.DERIVED_SUM ? 'Sum' : 'Average'
                  }`
                : 'Independent'
            }`}
          </TextLabel>
        </Flex>
      )}
      {showTags && (
        <Flex padding="8px 12px" alignItems="center">
          <Tooltip label="Tags" shouldWrapChildren>
            <Tags boxSize="24px" color="text.g2" fill="none" />
          </Tooltip>
          <Box
            // ml={'8px'}
            width="100%"
            sx={{
              '.okr-tags-popover > div > div:first-child': {
                border: 'none',
              },
            }}
          >
            <OKRTags
              tags={customTags}
              containerProps={{ maxWidth: '408px', className: 'okr-tags-popover' }}
              isReadOnly
              showNewTagCreation={false}
              tagNameLengthLimit={20}
              okrId={editOKRSavedDetails?.uuid}
            />
          </Box>
        </Flex>
      )}
      {showDescription && (
        <Flex padding="8px 12px" alignItems="center">
          <Tooltip label="Description" shouldWrapChildren>
            <Description boxSize="24px" color="text.g2" fill="none" />
          </Tooltip>
          <Tooltip label={description} shouldWrapChildren>
            <TextLabel
              ml="8px"
              color="text.g2"
              sx={{
                width: `calc(${primaryDrawerWidth}px - 130px)`,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                '-webkit-line-clamp': '4',
                '-webkit-box-orient': 'vertical',
              }}
            >
              {description}
            </TextLabel>
          </Tooltip>
        </Flex>
      )}
    </Box>
  );
};

export { OtherDetails };
